import React, { useState, useRef, useCallback } from 'react';
import { toPng } from 'html-to-image';
import { 
  X, 
  Download, 
  Copy, 
  Share2, 
  Check, 
  Loader2,
  EyeOff,
  Eye,
  Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';
import CreditResultsSnapshot from './CreditResultsSnapshot';

// Default social caption
const DEFAULT_CAPTION = `Credit Repair Results Receipt 🧾
We're grinding.`;

/**
 * Share Snapshot Modal
 * Premium modal for previewing and sharing Credit Results Snapshot
 */
const ShareSnapshotModal = ({ 
  isOpen, 
  onClose, 
  userData,
  scores,
  metrics,
  lastUpdated,
  theme
}) => {
  const [privacyMode, setPrivacyMode] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [captionCopied, setCaptionCopied] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const snapshotRef = useRef(null);

  // Download snapshot as PNG
  const handleDownload = useCallback(async () => {
    if (!snapshotRef.current) return;
    
    setIsGenerating(true);
    try {
      // Capture full snapshot without clipping
      const node = snapshotRef.current;
      const dataUrl = await toPng(node, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: '#ECFDF5',
        cacheBust: true,
        // Ensure we capture the full node dimensions
        width: node.scrollWidth,
        height: node.scrollHeight,
        style: {
          overflow: 'visible',
        },
      });
      
      // Create download link
      const link = document.createElement('a');
      link.download = `fundelite-credit-snapshot-${new Date().toISOString().split('T')[0]}.png`;
      link.href = dataUrl;
      link.click();
      
      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 2000);
    } catch (error) {
      console.error('Failed to generate snapshot:', error);
      alert('Failed to generate snapshot. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, []);

  // Copy caption to clipboard
  const handleCopyCaption = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(DEFAULT_CAPTION);
      setCaptionCopied(true);
      setTimeout(() => setCaptionCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy caption:', error);
    }
  }, []);

  // Share using Web Share API
  const handleShare = useCallback(async () => {
    if (!snapshotRef.current) return;
    
    // Check if Web Share API is supported
    if (!navigator.share || !navigator.canShare) {
      // Fallback: download the image
      handleDownload();
      return;
    }
    
    setIsGenerating(true);
    try {
      const node = snapshotRef.current;
      const dataUrl = await toPng(node, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: '#ECFDF5',
        cacheBust: true,
        width: node.scrollWidth,
        height: node.scrollHeight,
        style: {
          overflow: 'visible',
        },
      });
      
      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const file = new File([blob], 'fundelite-credit-snapshot.png', { type: 'image/png' });
      
      // Check if file sharing is supported
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Credit Results Snapshot',
          text: DEFAULT_CAPTION,
        });
      } else {
        // Fallback: share without file
        await navigator.share({
          title: 'Credit Results Snapshot',
          text: DEFAULT_CAPTION,
        });
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Failed to share:', error);
        // Fallback to download
        handleDownload();
      }
    } finally {
      setIsGenerating(false);
    }
  }, [handleDownload]);

  if (!isOpen) return null;

  const isDark = theme?.isDark;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-50 bg-[rgba(6,28,20,0.78)] backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="relative w-full max-w-lg max-h-[95vh] overflow-hidden rounded-3xl pointer-events-auto flex flex-col"
          style={{
            background: isDark 
              ? 'linear-gradient(180deg, #1A2F28 0%, #0F1F1A 100%)'
              : 'linear-gradient(180deg, #FFFFFF 0%, #F0FDF4 100%)',
            boxShadow: isDark
              ? '0 25px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.08)'
              : '0 25px 80px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div 
            className="px-5 py-4 border-b flex items-center justify-between flex-shrink-0"
            style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }}
          >
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ 
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  boxShadow: '0 4px 12px rgba(5,150,105,0.3)'
                }}
              >
                <Sparkles size={20} className="text-white" />
              </div>
              <div>
                <h2 
                  className="text-lg font-bold"
                  style={{ color: isDark ? '#F0FDF4' : '#065F46' }}
                >
                  Credit Results Snapshot
                </h2>
                <p 
                  className="text-xs"
                  style={{ color: isDark ? '#A7F3D0' : '#059669' }}
                >
                  Share your credit progress
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
              style={{ 
                background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                color: isDark ? '#A7F3D0' : '#6B7280'
              }}
            >
              <X size={20} />
            </button>
          </div>

          {/* Snapshot Preview - Scrollable wrapper with safe overflow */}
          <div 
            className="flex-1 overflow-y-auto p-4"
            style={{ minHeight: 0 }}
          >
            {/* Privacy Toggle */}
            <div className="flex items-center justify-end gap-2 mb-3">
              <span 
                className="text-xs font-medium"
                style={{ color: isDark ? '#A7F3D0' : '#6B7280' }}
              >
                Privacy Mode
              </span>
              <button
                onClick={() => setPrivacyMode(!privacyMode)}
                className="relative w-12 h-6 rounded-full transition-colors"
                style={{
                  background: privacyMode 
                    ? 'linear-gradient(135deg, #059669 0%, #047857 100%)' 
                    : isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
                }}
              >
                <div 
                  className="absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-transform"
                  style={{
                    transform: privacyMode ? 'translateX(28px)' : 'translateX(4px)',
                  }}
                />
                {privacyMode ? (
                  <EyeOff size={10} className="absolute top-1.5 left-1.5 text-white" />
                ) : (
                  <Eye size={10} className="absolute top-1.5 right-1.5 text-gray-500" />
                )}
              </button>
            </div>

            {/* Snapshot Container - No clipping, allows full card to be visible */}
            <div 
              className="flex justify-center items-start rounded-2xl p-3"
              style={{
                background: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)',
                minHeight: 'fit-content',
                overflow: 'visible',
              }}
            >
              <CreditResultsSnapshot
                ref={snapshotRef}
                userData={userData}
                scores={scores}
                metrics={metrics}
                privacyMode={privacyMode}
                lastUpdated={lastUpdated}
              />
            </div>

            {/* Caption Preview */}
            <div 
              className="mt-3 p-3 rounded-xl"
              style={{
                background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
              }}
            >
              <p 
                className="text-xs font-medium mb-2"
                style={{ color: isDark ? '#A7F3D0' : '#6B7280' }}
              >
                Suggested Caption:
              </p>
              <p 
                className="text-sm whitespace-pre-line"
                style={{ color: isDark ? '#F0FDF4' : '#1F2937' }}
              >
                {DEFAULT_CAPTION}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div 
            className="px-5 py-4 border-t flex gap-2 flex-shrink-0"
            style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }}
          >
            {/* Download Button */}
            <Button
              onClick={handleDownload}
              disabled={isGenerating}
              className="flex-1 h-11 rounded-xl font-semibold text-white"
              style={{
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                boxShadow: '0 4px 12px rgba(5,150,105,0.3)',
              }}
            >
              {isGenerating ? (
                <Loader2 size={18} className="mr-2 animate-spin" />
              ) : downloadSuccess ? (
                <Check size={18} className="mr-2" />
              ) : (
                <Download size={18} className="mr-2" />
              )}
              {downloadSuccess ? 'Downloaded!' : 'Download'}
            </Button>

            {/* Copy Caption Button */}
            <Button
              onClick={handleCopyCaption}
              variant="outline"
              className="h-11 px-4 rounded-xl font-semibold"
              style={{
                borderColor: isDark ? 'rgba(167,243,208,0.3)' : 'rgba(5,150,105,0.3)',
                color: isDark ? '#A7F3D0' : '#059669',
                background: isDark ? 'rgba(167,243,208,0.08)' : 'rgba(5,150,105,0.05)',
              }}
            >
              {captionCopied ? (
                <Check size={18} />
              ) : (
                <Copy size={18} />
              )}
            </Button>

            {/* Share Button */}
            <Button
              onClick={handleShare}
              disabled={isGenerating}
              variant="outline"
              className="h-11 px-4 rounded-xl font-semibold"
              style={{
                borderColor: isDark ? 'rgba(167,243,208,0.3)' : 'rgba(5,150,105,0.3)',
                color: isDark ? '#A7F3D0' : '#059669',
                background: isDark ? 'rgba(167,243,208,0.08)' : 'rgba(5,150,105,0.05)',
              }}
            >
              <Share2 size={18} />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShareSnapshotModal;
